import { useEffect, useMemo, useRef } from "react";
import { Icon } from "../../kit";
import { useCollapsed, useEditorStore, useUi } from "../../../state/editor/useEditor";
import { PINNED_PANEL } from "../basic/mode";
import { RIGHT_RAIL } from "../rails/railConfig";
import { ALL_GROUP_SECTIONS, tabSections } from "../sections";
import { InspectorSection } from "./InspectorSection";
import { CompactCheck, hiddenInert, isDevLabel } from "./rows";
import { filterAllTabs, markTerms } from "./search";
import type { SectionDef, TabId } from "./schema";
import { RAIL_W, RIGHT_PANEL_W } from "../layout";
import { ScrollArea } from "../ScrollArea";

/** Focus the Advanced search box from anywhere ("/" or ⌘K elsewhere). */
export const FOCUS_SEARCH_EVENT = "studio:focus-advanced-search";

/**
 * The right inspector panel: one always-visible search over every group, the
 * open group's sections, the Pinned view, and the "Show experimental" switch.
 */
export function RightPanel() {
  const [ui, patchUi] = useUi();
  const [, setCollapsed] = useCollapsed();
  const searchRef = useRef<HTMLInputElement>(null);
  const tab = ui.rightTab as TabId | typeof PINNED_PANEL | null;
  const pinnedView = tab === PINNED_PANEL;
  const meta = RIGHT_RAIL.find((r) => r.id === tab);

  const all = useMemo(() => (tab && !pinnedView ? tabSections(tab as TabId) : []), [tab, pinnedView]);
  const shown = all.filter((s) => ui.showExperimental || !s.experimental);
  // Sections AND rows the switch is holding back (dead, duplicate, debug).
  const hiddenCount =
    all.length -
    shown.length +
    (ui.showExperimental
      ? 0
      : shown.reduce((n, s) => n + s.rows.filter((r) => (r.inert && hiddenInert(r.inert)) || isDevLabel(r.label)).length, 0));

  const sources = useMemo(() => ALL_GROUP_SECTIONS.map((g) => ({ tabId: g.tabId, sections: g.sections })), []);
  // Experimental sections are searchable only while shown — a hit you cannot
  // open is worse than no hit.
  const visibleSources = useMemo(
    () => sources.map((s) => ({ ...s, sections: s.sections.filter((x) => ui.showExperimental || !x.experimental) })),
    [sources, ui.showExperimental],
  );
  const query = ui.search;
  const searching = query.trim().length > 0;
  const { hits, forceOpen, counts, rows } = useMemo(() => filterAllTabs(visibleSources, query), [visibleSources, query]);
  const mark = useMemo(() => (searching ? markTerms(query) : undefined), [searching, query]);

  const pinned = useMemo(() => pinnedSections(ui.pins), [ui.pins]);

  // Re-read what the panel displays when it opens: the viewer can change
  // scene and post settings without an event (a look applied, a file loaded),
  // and an on/off switch that disagrees with the render is a lie.
  const store = useEditorStore();
  const open = !!tab;
  useEffect(() => {
    if (!open) return;
    void store.refresh("postFX");
    void store.refresh("scene");
  }, [open, store]);

  useEffect(() => {
    const focus = () => { searchRef.current?.focus(); searchRef.current?.select(); };
    window.addEventListener(FOCUS_SEARCH_EVENT, focus);
    return () => window.removeEventListener(FOCUS_SEARCH_EVENT, focus);
  }, []);

  if (!tab || (!meta && !pinnedView)) return null;
  const title = pinnedView ? "Pinned" : meta!.label;
  const groupCount = Object.keys(counts).length;

  return (
    <div
      className="pointer-events-auto absolute top-0 bottom-0 z-10 overflow-hidden border-l border-ed-border bg-ed-panel"
      style={{ right: RAIL_W, width: RIGHT_PANEL_W }}
      data-advanced-panel={tab}
    >
      <div className="flex h-full flex-col">
        <div className="flex h-12 shrink-0 items-center gap-2 pl-4 pr-2">
          <span className="truncate text-[16px] font-semibold text-ed-text">{title}</span>
          <span className="flex-1" />
          <button
            title="Close"
            aria-label="Close panel"
            onClick={() => patchUi({ rightTab: null, lastRightTab: tab })}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-ed-text-3 hover:bg-ed-hover hover:text-ed-text"
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        {/* Always there: the one place to find any setting in any group. ⚠️
            Clearing must empty `ui.search`, because a non-empty query switches
            the whole body to cross-group hits. */}
        <div className="shrink-0 border-b border-ed-border px-3 pb-3">
          <div className="flex h-9 items-center gap-2 rounded-lg border border-ed-line bg-white px-2.5 focus-within:border-ed-accent focus-within:ring-2 focus-within:ring-ed-accent/20">
            <Icon name="search" size={17} className="shrink-0 text-ed-text-3" />
            <input
              ref={searchRef}
              value={query}
              placeholder="Search all settings"
              aria-label="Search all Advanced settings"
              onChange={(e) => patchUi({ search: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Escape") { patchUi({ search: "" }); e.currentTarget.blur(); }
              }}
              className="min-w-0 flex-1 bg-transparent text-[13px] text-ed-text outline-none placeholder:text-ed-text-3"
              data-advanced-search
              data-own-focus
            />
            {query ? (
              <button
                onClick={() => { patchUi({ search: "" }); searchRef.current?.focus(); }}
                title="Clear search"
                aria-label="Clear search"
                className="flex h-6 w-6 items-center justify-center rounded text-ed-text-3 hover:text-ed-text"
              >
                <Icon name="close" size={15} />
              </button>
            ) : (
              <kbd className="rounded border border-ed-border px-1.5 text-[11px] leading-[18px] text-ed-text-3">/</kbd>
            )}
          </div>
          {searching ? (
            <p className="mt-2 text-[12px] text-ed-text-3" aria-live="polite" data-search-count={rows}>
              {rows === 0
                ? "No settings match."
                : `${rows} setting${rows === 1 ? "" : "s"} in ${groupCount} group${groupCount === 1 ? "" : "s"}`}
            </p>
          ) : null}
        </div>
        <ScrollArea>
          {searching ? (
            hits.length === 0 ? (
              <EmptyState icon="search_off" title="Nothing matches that" body="Try another word — “glow”, “shadow”, “reflection”, “sparkle”. Search covers every group." />
            ) : (
              hits.map((hit, i) => {
                const owner = RIGHT_RAIL.find((r) => r.id === hit.tabId);
                const newGroup = i === 0 || hits[i - 1].tabId !== hit.tabId;
                return (
                  <div key={`${hit.tabId}.${hit.section.id}`}>
                    {newGroup && owner ? (
                      <button
                        onClick={() => patchUi({ rightTab: hit.tabId, lastRightTab: hit.tabId, lastAdvancedTab: hit.tabId, search: "" })}
                        title={`Open ${owner.label}`}
                        className="flex w-full items-center gap-2 border-b border-ed-border bg-ed-panel-2 px-3 py-1.5 text-left text-[12px] font-semibold text-ed-text-2 hover:text-ed-text"
                      >
                        <Icon name={owner.icon} size={15} />
                        <span className="truncate">{owner.label}</span>
                        <span className="font-normal text-ed-text-3">{counts[hit.tabId]}</span>
                        <span className="ml-auto shrink-0 font-normal text-ed-text-3">Open</span>
                      </button>
                    ) : null}
                    <InspectorSection tabId={hit.tabId} section={hit.section} forceOpen={forceOpen} mark={mark} />
                  </div>
                );
              })
            )
          ) : pinnedView ? (
            pinned.length === 0 ? (
              <EmptyState
                icon="star"
                title="Keep your go-to settings here"
                body="Hover any setting and click its star — or right-click it — to pin it. Pin a whole section from its ⋯ menu."
              />
            ) : (
              pinned.map((p) => <InspectorSection key={`${p.tabId}.${p.section.id}`} tabId={p.tabId} section={p.section} forceOpen />)
            )
          ) : shown.length === 0 ? (
            <EmptyState icon="inventory_2" title="Nothing here yet" body="" />
          ) : (
            shown.map((s) => (
              <InspectorSection
                key={s.id}
                tabId={tab as TabId}
                section={s}
                onCollapseOthers={() =>
                  shown.forEach((o) => { if (o.id !== s.id) setCollapsed(`${tab}.${o.id}`, true); })
                }
              />
            ))
          )}
        </ScrollArea>
        {!pinnedView ? (
          <label className="flex h-10 shrink-0 cursor-pointer items-center gap-2.5 border-t border-ed-border px-3 text-[12px] text-ed-text-2">
            <CompactCheck checked={ui.showExperimental} label="Show experimental sections" onChange={(v) => patchUi({ showExperimental: v })} />
            <span className="min-w-0 flex-1 truncate">Show experimental</span>
            {!ui.showExperimental && hiddenCount > 0 && !searching ? (
              <span className="shrink-0 text-ed-text-3">{hiddenCount} hidden here</span>
            ) : null}
          </label>
        ) : null}
      </div>
    </div>
  );
}

function EmptyState({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="flex flex-col items-center px-8 py-12 text-center">
      <Icon name={icon} size={28} className="text-ed-text-3" />
      <p className="mt-3 text-[13px] font-semibold text-ed-text">{title}</p>
      {body ? <p className="mt-1 text-[12px] leading-[1.45] text-ed-text-3">{body}</p> : null}
    </div>
  );
}

/**
 * The Pinned view: each pinned path resolved to its section, with only the
 * pinned rows (a whole-section pin, `group.section`, keeps every row). Paths
 * that no longer resolve — a section renamed away — are simply skipped.
 */
function pinnedSections(pins: string[]): { tabId: TabId; section: SectionDef }[] {
  const out: { tabId: TabId; section: SectionDef; rows: Set<string> | null }[] = [];
  for (const p of pins) {
    const [tabId, sectionId, rowId] = p.split(".");
    const section = tabSections(tabId as TabId).find((s) => s.id === sectionId);
    if (!section) continue;
    let entry = out.find((e) => e.tabId === tabId && e.section.id === sectionId);
    if (!entry) {
      entry = { tabId: tabId as TabId, section, rows: new Set() };
      out.push(entry);
    }
    if (!rowId) entry.rows = null;
    else if (entry.rows) entry.rows.add(rowId);
  }
  return out.map((e) => ({
    tabId: e.tabId,
    section: e.rows ? { ...e.section, rows: e.section.rows.filter((r) => e.rows!.has(r.id)) } : e.section,
  }));
}
